/**
 * CRM Settings — Pipelines & Stages
 *
 * Admin-facing CRUD for CRM pipelines and their stages.
 * Pipelines expand to an inline stage editor with drag-reorder
 * (via @dnd-kit) plus won/lost flags.
 */

import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ChevronDown, ChevronRight, GripVertical, Loader2, Plus, Star, Trash2 } from 'lucide-react'
import { type CSSProperties, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/crm/pipelines')({
  component: PipelinesPage,
})

interface Pipeline {
  id: string
  name: string
  description: string | null
  isDefault: boolean
  sortOrder: number
  active: boolean
}

interface Stage {
  id: string
  pipelineId: string
  key: string
  name: string
  description: string | null
  color: string | null
  probability: number
  isWon: boolean
  isLost: boolean
  sortOrder: number
  active: boolean
}

type DeleteDialogState =
  | { open: false }
  | { open: true; kind: 'pipeline'; id: string; name: string }
  | { open: true; kind: 'stage'; id: string; name: string; pipelineId: string }

function PipelinesPage() {
  const t = useTranslations()
  const qc = useQueryClient()
  const pipelinesQ = useQuery(orpc.crm.pipelines.list.queryOptions({ input: {} }))
  const pipelines = (pipelinesQ.data ?? []) as Pipeline[]

  const [expanded, setExpanded] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({ open: false })

  const invalidatePipelines = () =>
    qc.invalidateQueries({ queryKey: orpc.crm.pipelines.list.queryKey() })

  const createMut = useMutation(
    orpc.crm.pipelines.create.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.settings.pipelines.newPipeline'))
        setCreateOpen(false)
        setNewName('')
        setNewDescription('')
        invalidatePipelines()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const setDefaultMut = useMutation(
    orpc.crm.pipelines.setDefault.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.settings.pipelines.setDefault'))
        invalidatePipelines()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deletePipelineMut = useMutation(
    orpc.crm.pipelines.delete.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.common.delete'))
        invalidatePipelines()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deleteStageMut = useMutation(
    orpc.crm.stages.delete.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.common.delete'))
        // We don't know which pipelineId the deleted stage belonged to at
        // this scope, so invalidate the whole stages.list key prefix.
        qc.invalidateQueries({ queryKey: orpc.crm.stages.list.key() })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const confirmDelete = () => {
    if (!deleteDialog.open) return
    if (deleteDialog.kind === 'pipeline') {
      deletePipelineMut.mutate({ id: deleteDialog.id })
    } else {
      deleteStageMut.mutate({ id: deleteDialog.id })
    }
    setDeleteDialog({ open: false })
  }

  const deleteTitle = deleteDialog.open ? `${t('crm.common.delete')} "${deleteDialog.name}"?` : ''

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('crm.settings.pipelines.title')}</h1>
          <p className="text-muted-foreground">{t('navigation.crmSettings')}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('crm.settings.pipelines.newPipeline')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {pipelinesQ.isLoading ? (
            <div className="p-6 space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : pipelines.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              {t('crm.common.noRecords')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead>{t('crm.account.name')}</TableHead>
                  <TableHead>{t('crm.account.description')}</TableHead>
                  <TableHead>{t('crm.common.status')}</TableHead>
                  <TableHead className="text-right">{t('crm.common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pipelines.map((p) => (
                  <PipelineRow
                    key={p.id}
                    pipeline={p}
                    expanded={expanded === p.id}
                    onToggle={() => setExpanded(expanded === p.id ? null : p.id)}
                    onSetDefault={() => setDefaultMut.mutate({ id: p.id })}
                    onDelete={() =>
                      setDeleteDialog({
                        open: true,
                        kind: 'pipeline',
                        id: p.id,
                        name: p.name,
                      })
                    }
                    onRequestStageDelete={(stage) =>
                      setDeleteDialog({
                        open: true,
                        kind: 'stage',
                        id: stage.id,
                        name: stage.name,
                        pipelineId: stage.pipelineId,
                      })
                    }
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('crm.settings.pipelines.newPipeline')}</DialogTitle>
            <DialogDescription>{t('navigation.crmSettings')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="p-name">{t('crm.account.name')}</Label>
              <Input
                id="p-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Sales"
              />
            </div>
            <div>
              <Label htmlFor="p-desc">{t('crm.account.description')}</Label>
              <Textarea
                id="p-desc"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder={t('crm.common.optional')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t('crm.common.cancel')}
            </Button>
            <Button
              onClick={() =>
                createMut.mutate({
                  name: newName,
                  description: newDescription || undefined,
                })
              }
              disabled={!newName || createMut.isPending}
            >
              {createMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('crm.common.create')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => {
          if (!open) setDeleteDialog({ open: false })
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{deleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>{t('crm.common.required')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('crm.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              onClick={confirmDelete}
            >
              {t('crm.common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function PipelineRow({
  pipeline,
  expanded,
  onToggle,
  onSetDefault,
  onDelete,
  onRequestStageDelete,
}: {
  pipeline: Pipeline
  expanded: boolean
  onToggle: () => void
  onSetDefault: () => void
  onDelete: () => void
  onRequestStageDelete: (stage: Stage) => void
}) {
  const t = useTranslations()
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </TableCell>
        <TableCell className="font-medium">{pipeline.name}</TableCell>
        <TableCell className="text-muted-foreground max-w-md truncate">
          {pipeline.description ?? '-'}
        </TableCell>
        <TableCell>
          {pipeline.isDefault ? (
            <Badge className="bg-amber-500">{t('crm.settings.pipelines.defaultBadge')}</Badge>
          ) : pipeline.active ? (
            <Badge variant="outline">{t('crm.settings.lookups.active')}</Badge>
          ) : (
            <Badge variant="secondary">{t('crm.common.status')}</Badge>
          )}
        </TableCell>
        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-2">
            {!pipeline.isDefault && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onSetDefault}
                title={t('crm.settings.pipelines.setDefault')}
              >
                <Star className="h-4 w-4" />
              </Button>
            )}
            <Button size="sm" variant="ghost" className="text-red-600" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/30 p-4">
            <StagesEditor pipelineId={pipeline.id} onRequestStageDelete={onRequestStageDelete} />
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function StagesEditor({
  pipelineId,
  onRequestStageDelete,
}: {
  pipelineId: string
  onRequestStageDelete: (stage: Stage) => void
}) {
  const t = useTranslations()
  const qc = useQueryClient()
  const stagesQ = useQuery(orpc.crm.stages.list.queryOptions({ input: { pipelineId } }))
  const serverStages = (stagesQ.data ?? []) as Stage[]

  // Local order mirrors the server list but lets us optimistically re-order
  // during drag. We re-sync whenever the server data actually changes.
  const [localStages, setLocalStages] = useState<Stage[]>(serverStages)
  useEffect(() => {
    setLocalStages(serverStages)
  }, [serverStages])

  const invalidateStages = () =>
    qc.invalidateQueries({ queryKey: orpc.crm.stages.list.queryKey({ input: { pipelineId } }) })

  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState({
    key: '',
    name: '',
    color: '',
    probability: 0,
  })

  const createMut = useMutation(
    orpc.crm.stages.create.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.settings.pipelines.newStage'))
        setCreateOpen(false)
        setForm({ key: '', name: '', color: '', probability: 0 })
        invalidateStages()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const updateMut = useMutation(
    orpc.crm.stages.update.mutationOptions({
      onSuccess: () => invalidateStages(),
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const reorderMut = useMutation(
    orpc.crm.stages.reorder.mutationOptions({
      onSuccess: () => invalidateStages(),
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  // Same activation constraint the kanban board uses so a click on an input
  // inside the row doesn't accidentally trigger a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = localStages.findIndex((s) => s.id === active.id)
    const newIndex = localStages.findIndex((s) => s.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    const reordered = arrayMove(localStages, oldIndex, newIndex)
    setLocalStages(reordered)
    reorderMut.mutate({ pipelineId, ids: reordered.map((s) => s.id) })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">{t('crm.settings.pipelines.stages')}</h3>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          {t('crm.settings.pipelines.newStage')}
        </Button>
      </div>
      {stagesQ.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : localStages.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">{t('crm.common.noRecords')}</p>
      ) : (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead>{t('crm.settings.pipelines.stageName')}</TableHead>
                <TableHead className="w-32">{t('crm.settings.pipelines.stageColor')}</TableHead>
                <TableHead className="w-28">
                  {t('crm.settings.pipelines.stageProbability')}
                </TableHead>
                <TableHead className="w-20">{t('crm.settings.pipelines.isWon')}</TableHead>
                <TableHead className="w-20">{t('crm.settings.pipelines.isLost')}</TableHead>
                <TableHead className="w-20 text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              <SortableContext
                items={localStages.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {localStages.map((s) => (
                  <SortableStageRow
                    key={s.id}
                    stage={s}
                    onUpdate={(patch) => updateMut.mutate({ id: s.id, ...patch })}
                    onDelete={() => onRequestStageDelete(s)}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </DndContext>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('crm.settings.pipelines.newStage')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="s-key">{t('crm.settings.lookups.key')}</Label>
              <Input
                id="s-key"
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
                placeholder="qualified_lead"
              />
              <p className="text-xs text-muted-foreground mt-1">
                lowercase, underscores, starts with a letter
              </p>
            </div>
            <div>
              <Label htmlFor="s-name">{t('crm.settings.pipelines.stageName')}</Label>
              <Input
                id="s-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Qualified Lead"
              />
            </div>
            <div>
              <Label htmlFor="s-color">{t('crm.settings.pipelines.stageColor')}</Label>
              <Input
                id="s-color"
                type="color"
                value={form.color || '#888888'}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="s-prob">{t('crm.settings.pipelines.stageProbability')}</Label>
              <Input
                id="s-prob"
                type="number"
                min={0}
                max={100}
                value={form.probability}
                onChange={(e) => setForm({ ...form, probability: Number(e.target.value) })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t('crm.common.cancel')}
            </Button>
            <Button
              onClick={() =>
                createMut.mutate({
                  pipelineId,
                  key: form.key,
                  name: form.name,
                  color: form.color || undefined,
                  probability: form.probability,
                })
              }
              disabled={!form.key || !form.name || createMut.isPending}
            >
              {createMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('crm.common.create')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface StageUpdatePatch {
  name?: string
  color?: string
  probability?: number
  isWon?: boolean
  isLost?: boolean
}

function SortableStageRow({
  stage,
  onUpdate,
  onDelete,
}: {
  stage: Stage
  onUpdate: (patch: StageUpdatePatch) => void
  onDelete: () => void
}) {
  const t = useTranslations()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.id,
  })

  // Inline transform string (keeps us off `@dnd-kit/utilities`, which isn't
  // a direct dependency). Sortable only translates, so no scale/rotation.
  const style: CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell>
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
          aria-label={t('crm.settings.pipelines.stageName')}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell>
        <Input
          defaultValue={stage.name}
          onBlur={(e) => {
            if (e.target.value !== stage.name) {
              onUpdate({ name: e.target.value })
            }
          }}
        />
      </TableCell>
      <TableCell>
        <Input
          type="color"
          defaultValue={stage.color ?? '#888888'}
          onBlur={(e) => {
            if (e.target.value !== stage.color) {
              onUpdate({ color: e.target.value })
            }
          }}
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          min={0}
          max={100}
          defaultValue={stage.probability}
          onBlur={(e) => {
            const v = Number(e.target.value)
            if (v !== stage.probability) {
              onUpdate({ probability: v })
            }
          }}
        />
      </TableCell>
      <TableCell>
        <Checkbox
          checked={stage.isWon}
          onCheckedChange={(checked) => onUpdate({ isWon: Boolean(checked) })}
        />
      </TableCell>
      <TableCell>
        <Checkbox
          checked={stage.isLost}
          onCheckedChange={(checked) => onUpdate({ isLost: Boolean(checked) })}
        />
      </TableCell>
      <TableCell className="text-right">
        <Button size="sm" variant="ghost" className="text-red-600" onClick={onDelete}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  )
}
